import {zodResolver} from "@hookform/resolvers/zod";
import OrdersIcon from "@mui/icons-material/ShoppingCartRounded";
import {Alert, Box, Checkbox, FormControlLabel, Grid, Stack, Tooltip, Typography} from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import {DatePicker} from "@mui/x-date-pickers/DatePicker";
import dayjs, {Dayjs} from "dayjs";
import {TFunction} from "i18next";
import {useAtom} from "jotai";
import _ from "lodash";
import {MaterialReactTable, MRT_ColumnDef, useMaterialReactTable} from "material-react-table";
import React, {useEffect, useMemo, useState} from "react";
import {useForm} from "react-hook-form";
import {useTranslation} from "react-i18next";
import {v4 as uuidv4} from "uuid";
import {z} from "zod";
import {swallowException} from "../../utils/appUtils";
import {DEFAULT_SUGGESTED_PRICE_PER_HA_EUR, FIELD_INITIAL_VALUES} from "../../utils/constants";
import {nextYearMinusDay} from "../../utils/dateUtils";
import {toFieldPostBody} from "../../utils/fieldUtils";
import {calcAreaHa, createFeatureCollection} from "../../utils/mapUtils";
import {useCrops} from "../../hooks/cropsHooks";
import {useFieldManagement} from "../../hooks/fieldManagementHooks";
import {useCompanyFarms} from "../../hooks/farmsHooks";
import {useIrrigations} from "../../hooks/irrigationsHooks";
import {useOrderManagement} from "../../hooks/ordersHooks";
import {useSoils} from "../../hooks/soilsHooks";
import {Field, FieldWithoutID} from "../../types/domain/field";
import {Farm} from "../../types/domain/farm";
import {Order, PostOrderBodyType} from "../../types/domain/order";
import {getResourceErrorMessage, isResourceFailed} from "../../utils/resourceUtils";
import {AutocompleteInput} from "../base/AutocompleteInput";
import {getDefaultMRTOptions} from "../base/BaseTable";
import {DatePickerInput} from "../base/DatePickerInput";
import {Dialog} from "../base/Dialog";
import {Frame} from "../base/Frame";
import {LabeledInput} from "../base/LabeledInput";
import {TextInput} from "../base/TextInput";
import {FieldsShapeFileUploadForm} from "../fields/FieldsShapeFileUploadForm";
import {OrderMap} from "../orders/OrderMap";
import {authenticatedUserTableLocaleAtom} from "../../store/authenticatedUserStore";
import {convertToHasIdWithName} from "../../utils/fieldCreateUtils";
import analytics from "../../services/analyticsService";

export interface Props {
    open: boolean;
    onClose: (isSuccessful: boolean, companyResponse?: Order) => void;
    companyId: string;
}

type Setter = React.Dispatch<React.SetStateAction<Field[]>>;

export const OrderBulkCreateDialog: React.FC<Props> = ({open, onClose, companyId}) => {
    const {t} = useTranslation();
    const {soils} = useSoils();
    const {irrigations} = useIrrigations();
    const {crops} = useCrops();
    const orderManagement = useOrderManagement();
    const fieldManagement = useFieldManagement();
    const [selectedFarm, setSelectedFarm] = useState<Farm | null>(null);
    const [fields, setFields] = useState<Field[]>([]);
    const [areaInHa, setAreaInHa] = useState(0);

    const [fieldsFromShape, setFieldsFromShape] = useState<Field[]>([]);
    const [shouldCreateOrder, setShouldCreateOrder] = useState<boolean>(true);

    const farmsState = useCompanyFarms(companyId);
    const farms = useMemo(
        () => farmsState?.data?.farms?.filter((farm) => farm.companyId === companyId) ?? [],
        [farmsState?.data?.farms, companyId],
    );

    const initFormValues = INITIAL_FORM_VALUES;
    const {
        control,
        handleSubmit,
        watch,
        setValue,
        register,
        formState: {errors},
    } = useForm({
        defaultValues: initFormValues,
        resolver: zodResolver(ORDER_CREATE_SCHEMA),
    });
    const startDate = watch("startDate");
    const price = watch("price");
    const discount = watch("discount");
    const discountAsFraction = discount / 100;
    const orderId = useMemo(() => {
        return uuidv4();
    }, []);
    const createColumn = (
        t: TFunction<string, undefined>,
        header: string,
        key: keyof Field,
        size: number,
        maxSize: number,
        fields: Field[],
        setFields: Setter,
    ): MRT_ColumnDef<Field> => ({
        accessorKey: key,
        header: "",
        Header: () => (
            <Tooltip title={t(header)} arrow>
                <Typography
                    sx={{
                        textWrap: "wrap",
                        whiteSpace: "wrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        width: `${size}px`,
                        maxWidth: `${maxSize}px`,
                        textTransform: "uppercase",
                        fontWeight: 500,
                        fontSize: "12px",
                    }}
                >
                    {t(header)}
                </Typography>
            </Tooltip>
        ),
        muiTableHeadCellProps: () => ({
            sx: {
                "& .MuiTableCell-root": {
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    maxWidth: `${maxSize}px`,
                },
                textWrap: "wrap",
                whiteSpace: "wrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                width: `${size}px`,
                maxWidth: `${maxSize}px`,
                bgcolor: "#F3F5FA",
                paddingBottom: "1rem",
                "& .Mui-TableHeadCell-Content-Labels": {
                    textTransform: "uppercase",
                    fontWeight: "500",
                },
            },
        }),
        size,
        maxSize,

        muiEditTextFieldProps: ({row}) => ({
            type: "text",
            placeholder: "",
            onBlur: (event) => {
                const updatedFields = [...fields];
                const newField = updatedFields[row.index];

                //@ts-expect-error we are using this only for numeric props, but TS doesn't know about it.
                newField[key] = event.target.value;
                setFields(updatedFields);
            },
        }),
    });

    useEffect(() => {
        if (startDate) {
            const endDate = nextYearMinusDay(dayjs());
            setValue("endDate", endDate);
        }
    }, [startDate, setValue]);

    useEffect(() => {
        if (farms.length > 0) {
            const defaultFarms = farms.filter((item) => item.isDefault);
            const _defaultFarm = defaultFarms.length > 0 ? defaultFarms[0] : null;
            setSelectedFarm(_defaultFarm);
        }
    }, [farms]);

    useEffect(() => {
        for (const item of fieldsFromShape) {
            if (item.farmId && farms.some((f) => f.id === item.farmId)) continue;

            item.farmId = selectedFarm ? selectedFarm.id : null;
        }
        setFields(fieldsFromShape);
    }, [selectedFarm, fieldsFromShape, farms]);

    useEffect(() => {
        let overAllArea = 0;
        for (const field of fields) {
            overAllArea += field ? calcAreaHa(field) : 0;
        }
        setAreaInHa(_.round(overAllArea, 2));
        const suggestedPrice = _.round(overAllArea * DEFAULT_SUGGESTED_PRICE_PER_HA_EUR, 2);
        setValue("price", suggestedPrice);
    }, [fields, setValue]);

    useEffect(() => {
        const finalPrice = Math.max(0, _.round(price - price * discountAsFraction, 2));
        setValue("finalPrice", finalPrice);
    }, [price, discountAsFraction, setValue]);

    /* eslint-disable react-hooks/exhaustive-deps */
    const columns: MRT_ColumnDef<Field>[] = useMemo(
        () => [
            createColumn(t, "dialogs.order_bulk_create.columns.name", "name", 140, 180, fields, setFields),
            {
                header: t("dialogs.order_bulk_create.columns.crop"),
                size: 140,
                maxSize: 240,
                enableEditing: false,
                Cell: ({row}) => (
                    <AutocompleteInput
                        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-explicit-any
                        name={`cropId_${row.index}` as any}
                        control={control}
                        options={crops}
                        initialValue={row.original.cropCycles ? row.original.cropCycles[0].cropId : null}
                        getOptionLabel={(item) => item.name}
                        onChange={(value) => {
                            const updatedFields = [...fields];
                            updatedFields[row.index].cropCycles[0].cropId = value
                                ? (value as string)
                                : FIELD_INITIAL_VALUES.cropCycles[0].cropId;
                            setFields(updatedFields);
                        }}
                    />
                ),
            },
            {
                header: t("dialogs.order_bulk_create.columns.soil"),
                size: 140,
                maxSize: 240,
                enableEditing: false,
                Cell: ({row}) => (
                    <AutocompleteInput
                        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-explicit-any
                        name={`soilId_${row.index}` as any}
                        control={control}
                        options={soils}
                        initialValue={row.original.soilId}
                        getOptionLabel={(item) => item.name}
                        onChange={(value) => {
                            const updatedFields = [...fields];
                            updatedFields[row.index].soilId = value ? (value as string) : FIELD_INITIAL_VALUES.soilId;
                            setFields(updatedFields);
                        }}
                    />
                ),
            },
            {
                header: t("dialogs.order_bulk_create.columns.irrigation_type"),
                size: 140,
                maxSize: 140,
                enableEditing: false,
                Cell: ({row}) => (
                    <AutocompleteInput
                        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-explicit-any
                        name={`irrigationTypeId_${row.index}` as any}
                        control={control}
                        options={irrigations}
                        initialValue={row.original.irrigationTypeId}
                        getOptionLabel={(item) => item.name}
                        onChange={(value) => {
                            const updatedFields = [...fields];
                            updatedFields[row.index].irrigationTypeId = value
                                ? (value as string)
                                : FIELD_INITIAL_VALUES.irrigationTypeId;
                            setFields(updatedFields);
                        }}
                    />
                ),
            },
            createColumn(
                t,
                "dialogs.field_create.form.sat_soil_moisture_bias",
                "satSoilMoistBias",
                55,
                70,
                fields,
                setFields,
            ),
            {
                header: t("dialogs.field_create.form.drought_sensitivity"),
                size: 55,
                maxSize: 77,
                enableEditing: false,
                Cell: ({row}) => (
                    <AutocompleteInput
                        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-explicit-any
                        name={`droughtSensitivity_${row.index}` as any}
                        control={control}
                        options={convertToHasIdWithName([1, 2, 3, 4, 5])}
                        initialValue={row.original.cropCycles ? row.original.cropCycles[0].droughtSensitivity : null}
                        getOptionLabel={(option) => option.name}
                        onChange={(value) => {
                            const updatedFields = [...fields];
                            updatedFields[row.index].cropCycles[0].droughtSensitivity = value
                                ? (value as number)
                                : FIELD_INITIAL_VALUES.cropCycles[0].droughtSensitivity;
                            setFields(updatedFields);
                        }}
                    />
                ),
            },
            createColumn(
                t,
                "dialogs.field_create.form.irrigation_interval",
                "irrigationInterval",
                55,
                90,
                fields,
                setFields,
            ),
            createColumn(
                t,
                "dialogs.field_create.form.moisture_level",
                "desiredMoistureLevel",
                55,
                70,
                fields,
                setFields,
            ),
            createColumn(
                t,
                "dialogs.field_create.form.irrigation_criteria",
                "irrigationCriteria",
                55,
                70,
                fields,
                setFields,
            ),
            createColumn(
                t,
                "dialogs.field_create.form.irrigation_default",
                "irrigationDefault",
                55,
                70,
                fields,
                setFields,
            ),

            {
                header: t("dialogs.order_bulk_create.columns.farm"),
                size: 140,
                maxSize: 200,
                enableEditing: false,
                Cell: ({row}) => (
                    <AutocompleteInput
                        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-explicit-any
                        name={`farmId_${row.index}` as any}
                        control={control}
                        options={farms}
                        isLoading={farmsState.isLoading}
                        initialValue={row.original.farmId}
                        getOptionLabel={(item) => item.name}
                        onChange={(value) => {
                            const updatedFields = [...fields];
                            updatedFields[row.index].farmId = value ? (value as string) : null;
                            setFields(updatedFields);
                        }}
                    />
                ),
            },
            {
                header: t("dialogs.order_bulk_create.columns.planted_date"),
                size: 140,
                maxSize: 140,
                enableEditing: false,
                Cell: ({row}) => {
                    return (
                        <DatePicker
                            slotProps={{
                                textField: {
                                    variant: "filled",
                                    fullWidth: true,
                                    InputProps: {
                                        disableUnderline: true,
                                    },
                                },
                            }}
                            value={dayjs(row.original.cropCycles[0].plantedDate)}
                            onChange={(date) => {
                                if (date) {
                                    const updatedFields = [...fields];
                                    updatedFields[row.index].cropCycles[0].plantedDate = date;
                                    setFields(updatedFields);
                                }
                            }}
                        />
                    );
                },
            },
            {
                header: t("dialogs.order_bulk_create.columns.harvested_date"),
                size: 140,
                maxSize: 140,
                enableEditing: false,
                Cell: ({row}) => {
                    return (
                        <DatePicker
                            slotProps={{
                                textField: {
                                    variant: "filled",
                                    fullWidth: true,
                                    InputProps: {
                                        disableUnderline: true,
                                    },
                                },
                            }}
                            value={dayjs(row.original.cropCycles[0].estHarvestDate)}
                            onChange={(date) => {
                                if (date) {
                                    const updatedFields = [...fields];
                                    updatedFields[row.index].cropCycles[0].estHarvestDate = date;
                                    setFields(updatedFields);
                                }
                            }}
                        />
                    );
                },
            },
        ],
        [fields],
    );

    const [locale] = useAtom(authenticatedUserTableLocaleAtom);
    const defaultTableOptions = getDefaultMRTOptions<Field>(locale);
    const fieldsTable = useMaterialReactTable({
        ...defaultTableOptions,
        columns,
        data: fields,
        editDisplayMode: "table",
        enableEditing: true,
        enableGlobalFilter: false,
        enableSorting: false,
        enableTopToolbar: false,
        enableRowActions: false,
        initialState: {
            ...defaultTableOptions.initialState,
            showColumnFilters: false,
            density: "compact",
        },
        muiTableHeadCellProps: {
            sx: {
                textWrap: "wrap",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                width: "90px",
                maxWidth: "140px",
                textTransform: "uppercase",
                fontWeight: 500,
                fontSize: "12px",
                bgcolor: "#F3F5FA",
                paddingBottom: "1rem",
                "& .Mui-TableHeadCell-Content-Labels": {
                    textTransform: "uppercase",
                    fontWeight: "500",
                },
            },
        },
    });
    const onSubmit = (data: FormInputs) => {
        orderManagement.reset();
        analytics
            .track("field-create", {
                variation: "bulk",
                fields: fields.length ?? 0,
            })
            .catch(swallowException);
        let apiCall;
        if (shouldCreateOrder) {
            const orderBody = formInputsToPostOrderBody(data, fields);
            orderBody.id = orderId;
            apiCall = orderManagement.create(companyId, orderBody);
        } else {
            const fielsApiCalls = fields.map((field) => {
                return fieldManagement.create(companyId, toFieldPostBody(field));
            });
            apiCall = Promise.all(fielsApiCalls).catch(swallowException);
        }
        apiCall.then(() => onClose(true)).catch(swallowException);
    };

    return (
        <Dialog
            enableSubmit
            enableDelete={false}
            deleteTitle={t("dialogs.actions.delete")}
            submitTitle={t("dialogs.actions.submit")}
            title={t("dialogs.order_bulk_create.title")}
            titleIcon={<OrdersIcon color="primary" />}
            open={open}
            onClose={() => onClose(false)}
            // eslint-disable-next-line @typescript-eslint/no-misused-promises
            onSubmit={handleSubmit(onSubmit)}
            width="calc(100% - 3rem)"
        >
            <Stack direction="column" spacing={2} useFlexGap>
                {fields &&
                    fields.length > 0 &&
                    (() => {
                        const geojson = createFeatureCollection(fields);
                        return <OrderMap geojson={geojson} fields={fields} />;
                    })()}
                <form>
                    {isResourceFailed(orderManagement.resource) && (
                        <Alert severity="error">{getResourceErrorMessage(orderManagement.resource)}</Alert>
                    )}

                    <Stack direction="column" spacing={2} useFlexGap>
                        <Grid container spacing={2}>
                            <Grid item xs={6}>
                                <FieldsShapeFileUploadForm setFields={setFieldsFromShape} />
                            </Grid>
                            <Grid item xs={6}>
                                <LabeledInput label={t("dialogs.order_bulk_create.form.farm")}>
                                    <Autocomplete
                                        clearIcon={null}
                                        autoSelect={true}
                                        options={farms}
                                        getOptionLabel={(option) => option.name}
                                        value={selectedFarm}
                                        onChange={(_, value) => {
                                            setSelectedFarm(value);
                                        }}
                                        isOptionEqualToValue={(option, value) => option.id === value.id}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                variant="filled"
                                                hiddenLabel
                                                margin="none"
                                                size="small"
                                                fullWidth
                                                InputProps={{
                                                    ...params.InputProps,
                                                    disableUnderline: true,
                                                }}
                                            />
                                        )}
                                    />
                                </LabeledInput>
                            </Grid>
                        </Grid>

                        <FormControlLabel
                            label={t("dialogs.order_bulk_create.form.create_order")}
                            control={
                                <Checkbox
                                    sx={{
                                        paddingY: 0,
                                    }}
                                    onChange={(event) => {
                                        setShouldCreateOrder(event.target.checked);
                                    }}
                                    checked={!!shouldCreateOrder}
                                />
                            }
                            sx={{mt: "1rem"}}
                        />

                        {shouldCreateOrder && (
                            <Grid item xs={12}>
                                <Frame>
                                    <Typography fontSize={16} textTransform="uppercase" sx={{mb: "1rem"}}>
                                        {t("dialogs.order_bulk_create.form.order_information")}
                                    </Typography>
                                    <Grid container spacing={2}>
                                        <Grid item xs={12}>
                                            <Typography>{t("dialogs.order_bulk_create.form.order_number")}</Typography>
                                            <TextField
                                                fullWidth
                                                variant="filled"
                                                defaultValue={`# ${orderId.slice(0, 8)}`}
                                                InputProps={{
                                                    disableUnderline: true,
                                                    readOnly: true,
                                                    sx: {
                                                        textTransform: "uppercase",
                                                    },
                                                }}
                                            ></TextField>
                                            <Typography border={1} borderColor="secondary.main"></Typography>
                                        </Grid>
                                    </Grid>
                                    <Grid container spacing={2}>
                                        <Grid item xs={12}>
                                            <Grid container spacing={2}>
                                                <Grid item xs={6}>
                                                    <LabeledInput
                                                        label={t("dialogs.order_bulk_create.form.start_date")}
                                                    >
                                                        <DatePickerInput
                                                            name="startDate"
                                                            control={control}
                                                            errors={errors}
                                                        />
                                                    </LabeledInput>
                                                </Grid>
                                                <Grid item xs={6}>
                                                    <LabeledInput label={t("dialogs.order_bulk_create.form.end_date")}>
                                                        <DatePickerInput
                                                            name="endDate"
                                                            control={control}
                                                            errors={errors}
                                                        />
                                                    </LabeledInput>
                                                </Grid>
                                            </Grid>
                                        </Grid>
                                        <Grid item xs={12}>
                                            <Grid container spacing={2}>
                                                <Grid item xs={4}>
                                                    <LabeledInput label={t("dialogs.order_bulk_create.form.price")}>
                                                        <TextInput
                                                            name="price"
                                                            type="number"
                                                            register={register}
                                                            errors={errors}
                                                        />
                                                    </LabeledInput>
                                                </Grid>
                                                <Grid item xs={4}>
                                                    <LabeledInput label={t("dialogs.order_bulk_create.form.discount")}>
                                                        <TextInput
                                                            name="discount"
                                                            type="number"
                                                            register={register}
                                                            errors={errors}
                                                        />
                                                    </LabeledInput>
                                                </Grid>
                                                <Grid item xs={4}>
                                                    <LabeledInput
                                                        label={t("dialogs.order_bulk_create.form.final_price")}
                                                    >
                                                        <TextInput
                                                            name="finalPrice"
                                                            type="number"
                                                            register={register}
                                                            errors={errors}
                                                        />
                                                    </LabeledInput>
                                                </Grid>
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                </Frame>
                            </Grid>
                        )}
                        {fields.length > 0 && (
                            <Box>
                                <Stack direction="row" spacing={2}>
                                    <Typography variant="caption" display="block" gutterBottom>
                                        Overall Fields Area:
                                    </Typography>
                                    <Typography variant="body1">{areaInHa}</Typography>
                                </Stack>
                                <MaterialReactTable table={fieldsTable} />
                            </Box>
                        )}
                    </Stack>
                </form>
            </Stack>
        </Dialog>
    );
};

interface FormInputs {
    price: number;
    discount: number;
    finalPrice: number;
    startDate: Dayjs;
    endDate: Dayjs;
    fields?: Field[];
}

const INITIAL_FORM_VALUES: FormInputs = {
    price: 0,
    discount: 0,
    finalPrice: 0,
    startDate: dayjs(),
    endDate: nextYearMinusDay(dayjs()),
    fields: [],
};

const ORDER_CREATE_SCHEMA = z.object({
    price: z.number().nonnegative(),
    discount: z.number().nonnegative().min(0).max(100),
    finalPrice: z.number().nonnegative(),
    startDate: z.instanceof(dayjs as unknown as typeof Dayjs),
    endDate: z.instanceof(dayjs as unknown as typeof Dayjs),
});

const formInputsToPostOrderBody = (inputs: FormInputs, fields: FieldWithoutID[]) => {
    const subscriptionType = "Standart";
    const _fields = fields.map(toFieldPostBody);

    const order: PostOrderBodyType = {
        price: inputs.price,
        subscriptionType,
        discount: inputs.discount,
        finalPrice: inputs.finalPrice,
        startDate: inputs.startDate.format("YYYY-MM-DD"),
        endDate: inputs.endDate.format("YYYY-MM-DD"),
        fields: _fields,
    };
    return order;
};
